// Libs
import { useCallback, useState } from "react";
import { isEmpty } from "lodash";

// Types
import type { TimeRange } from "src/types/timeRange";
import type { StaticRange } from "src/types/staticRange";

// Utils
import { formatDate, FORMAT_DATE_STD } from "src/utils/formatters";

const CALENDAR_DEFAULT_VALUE = {};

function timeRangeToCalendarValue(timeRange: TimeRange) {
    if (!timeRange) {
        return undefined;
    }

    return {
        from: formatDate(timeRange.startDate, FORMAT_DATE_STD),
        to: formatDate(timeRange.endDate, FORMAT_DATE_STD),
    };
}

export type UseCalendarFilterValue = { from?: string; to?: string };

export interface UseCalendarFilterOptions {
    defaultTimeRange?: TimeRange;
    defaultValue?: UseCalendarFilterValue;
    initialValue?: UseCalendarFilterValue;
    staticRanges?: StaticRange[];
}

export interface UseCalendarFilterResult {
    calendar: UseCalendarFilterValue;
    handleCalendarChange: (range: TimeRange) => void;
    isEmpty: boolean;
    resetFilter: () => void;
    staticRanges?: StaticRange[];
    timeRange?: TimeRange;
}

/**
 * Hook to handle a Calendar filter
 * @param options Hooks options
 * @returns Helpers to handle Calendar filter
 */
export function useCalendarFilter({
    defaultValue = CALENDAR_DEFAULT_VALUE,
    defaultTimeRange,
    initialValue,
    staticRanges,
}: UseCalendarFilterOptions = {}): UseCalendarFilterResult {
    const [calendar, setCalendar] = useState<UseCalendarFilterValue>(
        initialValue ?? timeRangeToCalendarValue(defaultTimeRange) ?? defaultValue
    );

    const handleCalendarChange = useCallback(
        (range: TimeRange) => {
            setCalendar({
                from: formatDate(range.startDate, FORMAT_DATE_STD),
                to: formatDate(range.endDate, FORMAT_DATE_STD),
            });
        },
        [setCalendar]
    );

    const resetFilter = useCallback(
        () => setCalendar(timeRangeToCalendarValue(defaultTimeRange) ?? defaultValue),
        [setCalendar, defaultTimeRange, defaultValue]
    );

    return {
        calendar,
        isEmpty: isCalendarFilterEmpty(calendar),
        handleCalendarChange,
        resetFilter,
        staticRanges,
        timeRange: defaultTimeRange,
    };
}

/**
 * Format Calendar filter value
 * @param calendarValue Filter value
 * @returns Formatted filter value
 */
export function formatCalenderFilter(
    calendarValue: UseCalendarFilterValue = CALENDAR_DEFAULT_VALUE
): string {
    return `${calendarValue.from} - ${calendarValue.to}`;
}

/**
 * Compute if filter is empty or not
 * @param filter Filter value
 * @returns If is empty
 */
export function isCalendarFilterEmpty(
    filter: UseCalendarFilterValue = CALENDAR_DEFAULT_VALUE
): boolean {
    return isEmpty(filter.from) && isEmpty(filter.to);
}
